import React, { useEffect, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Box,
  Collapse,
  Typography,
  Tooltip,
  Popover,
} from "@mui/material";
import { LuChevronDown, LuChevronUp } from "react-icons/lu";
import TablePagination from "@/Components/Tables/Pagination";
import CheckboxBox from "@/Components/Form/CheckboxBox";
import InputTextField from "@/Components/Form/InputTextField";
import InputDropdown from "@/Components/Form/InputDropdown";
import { OutLinedButton } from "@/Components/Buttons/OutLinedButton";
import dayjs from "dayjs";

export default function TableComp({
  tableData = [],
  visibleFields = [],
  expandedFields = [],
  actions = [],
  actionLink = [],
  showAlphabetFilter = false,
  onRowClick, // <-- New prop
  onEdit,
  setSchemaKey
}) {
  const [expandedRow, setExpandedRow] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedLetter, setSelectedLetter] = useState(null); 
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverValue, setPopoverValue] = useState("");

  const handleIconClick = (event, value) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setPopoverValue(value);
  };
  const handlePopoverClose = () => {
    setAnchorEl(null);
    setPopoverValue("");
  };

  const getMutualSchemaKey = (tableData) => {
  if (!Array.isArray(tableData) || tableData.length === 0) return null;

  const frequencyMap = {};

  tableData.forEach((row) => {
    const prefixes = extractPrefixesFromRow(row);

    prefixes.forEach((p) => {
      if (!p) return;
      frequencyMap[p] = (frequencyMap[p] || 0) + 1;
    });
  });


  const sorted = Object.entries(frequencyMap).sort((a, b) => {
    if (b[1] === a[1]) {
      return b[0].length - a[0].length;
    }
    return b[1] - a[1];
  });

  return sorted[0]?.[0] || null;
};


  const extractPrefixesFromRow = (row) => {
    return Object.keys(row)
      .filter(
        (k) => k !== "id" && k !== "selected" && /[A-Z]/.test(k) // must be camelCase or PascalCase
      )
      .map((k) =>
        k.replace(
          /(Id|Status|Type|Date|UpdatedBy|CreatedBy|Description|Quantity|Unit|EntryDate|UpdateDate|By).*$/,
          ""
        )
      );
  };

  useEffect(() => {
    if (!tableData || tableData.length === 0) return;

    const selectedRow = tableData.find((row) => row.selected);
    if (selectedRow) {
      setSelectedRowId(selectedRow.id);
    } else {
      // Mark the last row as selected by default
      const firstRow = tableData[0];
      setSelectedRowId(firstRow.id);
      onRowClick?.(firstRow);
    }
    const schemaKey = getMutualSchemaKey(tableData);
    setSchemaKey(schemaKey)

  }, [tableData]);

  const getDisplayValue = (val) => {
    if (val === undefined || val === null || val === "") return "-";

    if (typeof val === "object") {
      // If it's an object with name or label, show only that
      if ("name" in val) return val.name;
      if ("label" in val) return val.label;
      return JSON.stringify(val); // fallback
    }

    // Otherwise return as string
    return val.toString();
  };

  const displayValue = (val) => {
    if (val === undefined || val === null || val === "") return "-";

    // Only try to parse ISO date strings
    // if (
    //   typeof val === "string" &&
    //   /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(val)
    // ) {
    //   const date = dayjs(val);
    //   if (date.isValid()) {
    //     return date.format("DD/MM/YYYY HH:mm"); // adjust format as needed
    //   }
    // }

    // Otherwise, return as string
    return val.toString();
  };
  const TableCellRenderer = ({ value, row }) => {
    // Handle simple text values
    if (!value || typeof value === "string" || typeof value === "number") {
      return (
        <Typography
          variant="bodySmallSemiBold"
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "normal",
          }}
          title={displayValue(value)}
        >
          {displayValue(value)}
        </Typography>
      );
    }

    // Handle custom component definitions
    switch (value.componentType) {
      case "checkbox":
        return (
          <CheckboxBox
            disabled={value.disabled}
            checked={value.checked || false}
            onChange={(e) => value.onChange?.(e.target.checked, row)}
            checkboxLabel={value.label || ""}
            sx={value.sx}
          />
        );

      case "textfield":
        return (
          <InputTextField
            value={value.value || ""}
            onChange={(e) => value.onChange?.(e.target.value, row)}
            placeholder={value.placeholder}
            sx={{ width: value.width || "100%", ...value.sx }}
          />
        );

      case "dropdown":
        return (
          <InputDropdown
            options={value.options || []}
            value={value.value || ""}
            onChange={(val) => value.onChange?.(val, row)}
            sx={{ width: value.width || "100%", ...value.sx }}
          />
        );

      case "button":
        return (
          <OutLinedButton
            label={value.label}
            onClick={() => value.onClick?.(row)}
            sx={value.sx}
            disabled={value.disabled}
          />
        );

      case "radio":
        return (
          <RadioGroup
            value={value.value || ""}
            onChange={(e) => value.onChange?.(e.target.value, row)}
            row
          >
            {value.options?.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio />}
                label={option.label}
              />
            ))}
          </RadioGroup>
        );

      default:
        return (
          <Typography variant="bodySmall">
            {value.value || JSON.stringify(value)}
          </Typography>
        );
    }
  };

  const toggleRow = (index) => {
    setExpandedRow((prev) => (prev === index ? null : index));
  };

  const normalizedFields = visibleFields.map((field) =>
    typeof field === "string"
      ? {
          key: field,
          label: field.charAt(0).toUpperCase() + field.slice(1),
          textAlign: "left",
        }
      : {
          key: field.key,
          label: field.label,
          width: field.width,
          truncate: field.truncate || false,
          textAlign: field.textAlign || "left",
          icon: field.icon,
          showAsIcon: field.showAsIcon,
        }
  );

  const showExpandColumn = expandedFields.length > 0;

  // Filter data based on selected letter
  const filteredData = selectedLetter
    ? tableData.filter((row) =>
        row.Name?.toString().toUpperCase().startsWith(selectedLetter)
      )
    : tableData;

  return (
    <Box>
      <Table>
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: "var(--brand-100)",
              borderTop: "1px solid var(--brand-200)",
              borderBottom: "1px solid var(--brand-200)",
            }}
          >
            {showExpandColumn && <TableCell width={25} />}
            {normalizedFields.map(({ key, label, width, textAlign }) => (
              <TableCell
                variant="bodySmall"
                key={key}
                sx={{
                  backgroundColor: "var(--brand-100)",
                  width: width ? `${width}px` : undefined,
                  fontFamily: "inter",
                  textAlign,
                  p: 2,
                }}
              >
                {label}
              </TableCell>
            ))}
            {(actions?.length > 0 ||
              tableData.some(
                (row) => Array.isArray(row?.actions) && row?.actions?.length > 0
              )) && (
              <TableCell
                variant="bodySmall"
                sx={{
                  backgroundColor: "var(--brand-100)",
                  width: "150px",
                }}
              >
                Actions
              </TableCell>
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          {filteredData
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((row, index) => {
              const actualIndex = page * rowsPerPage + index;
              const isLastRow =
                index ===
                Math.min(
                  rowsPerPage,
                  filteredData.length - page * rowsPerPage
                ) -
                  1;
              const normalizedRow = Object.fromEntries(
                Object.entries(row).map(([key, value]) => [key, value])
              );

              const visibleKeys = normalizedFields.map((f) => f.key);

              const displayValue = (val) => {
                if (val === undefined || val === null || val === "") return "-";

                // // Check if value is a valid date
                // const date = dayjs(val);
                // if (date.isValid()) {
                //   // Return formatted date
                //   return date.format("DD/MM/YYYY HH:mm"); // adjust format as needed
                // }

                // Otherwise, return string
                return val.toString();
              };

              const extraFields = (() => {
                if (expandedFields.length > 0) {
                  return expandedFields.map((field) => {
                    const rawValue = normalizedRow[field.key];

                    // Handle null or undefined
                    if (
                      rawValue === undefined ||
                      rawValue === null ||
                      rawValue === ""
                    )
                      return [field.label || field.key, "-"];

                    // If object, show name/label or stringify
                    if (typeof rawValue === "object") {
                      if ("name" in rawValue)
                        return [field.label || field.key, rawValue.name];
                      if ("label" in rawValue)
                        return [field.label || field.key, rawValue.label];
                      return [
                        field.label || field.key,
                        JSON.stringify(rawValue),
                      ];
                    }

                    // Otherwise, primitive value
                    return [field.label || field.key, rawValue.toString()];
                  });
                }

                return [];
              })();

              const hasExtraFields = extraFields.length > 0;

              return (
                <React.Fragment key={actualIndex}>
                  <TableRow
                    sx={{
                      borderBottom: isLastRow
                        ? "none"
                        : "1px solid var(--gray-200)",
                      cursor: onRowClick ? "pointer" : "default",
                      backgroundColor:
                        selectedRowId === row.id
                          ? "var(--brand-200)"
                          : "var(--brand-0)",
                    }}
                    onClick={() => {
                      setSelectedRowId(row.id);
                      onRowClick?.(row);
                    }}
                  >
                    {showExpandColumn && (
                      <TableCell>
                        {hasExtraFields ? (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation(); // prevent row click
                              toggleRow(actualIndex);
                            }}
                          >
                            {expandedRow === actualIndex ? (
                              <LuChevronUp
                                style={{
                                  fontSize: 20,
                                  color: "var(--brand-600)",
                                }}
                              />
                            ) : (
                              <LuChevronDown
                                style={{
                                  fontSize: 20,
                                  color: "var(--brand-600)",
                                }}
                              />
                            )}
                          </IconButton>
                        ) : (
                          <Box sx={{ width: 20, height: 20 }} />
                        )}
                      </TableCell>
                    )}

                    {normalizedFields.map((field) => {
                      const { key, width, textAlign, icon, showAsIcon } = field;
                      const cellValue = normalizedRow[key]; // value for this row & field

                      return (
                        <TableCell
                          key={key}
                          sx={{
                            color: "var(--brand-910)",
                            backgroundColor: "var(--brand-0)",
                            textAlign,
                            width: width ? `${width}px` : "200px",
                            maxWidth: "200px",
                            fontFamily: "inherit",
                          }}
                        >
                          {showAsIcon ? (
                            <IconButton
                              size="small"
                              onClick={(e) => handleIconClick(e, cellValue)}
                            >
                              {icon}
                            </IconButton>
                          ) : (
                            <TableCellRenderer
                              value={getDisplayValue(cellValue)}
                              row={row}
                            />
                          )}
                        </TableCell>
                      );
                    })}

                    {(row.actions?.length > 0 || actions?.length > 0) && (
                      <>
                        {row?.actions?.length > 0 ? (
                          <TableCell>
                            <Box display="flex" gap={2} alignItems="center">
                              {row?.actions?.map((action, idx) => {
                                if (action.type === "icon") {
                                  return (
                                    <Tooltip
                                      title={action.title || action.label || ""}
                                      key={idx}
                                    >
                                      <IconButton
                                        size="small"
                                        onClick={() => action.onClick?.(row)}
                                        color={
                                          action.color || "var(--brand-500)"
                                        }
                                      >
                                        {action.icon}
                                      </IconButton>
                                    </Tooltip>
                                  );
                                } else if (action.type === "text") {
                                  return (
                                    <Typography
                                      key={idx}
                                      variant="bodyMedium"
                                      sx={{
                                        color: "var(--link-blue)",
                                        cursor: "pointer",
                                      }}
                                      onClick={() => action.onClick?.(row)}
                                    >
                                      {action.label}
                                    </Typography>
                                  );
                                }
                                return null;
                              })}
                            </Box>
                          </TableCell>
                        ) : (
                          <TableCell>
                            <Box display="flex" gap={2}>
                              {actions
                                .filter(
                                  (action) => !action.show || action.show(row)
                                ) // ✅ only show when condition passes
                                .map(({ icon, title, onClick }, idx) => (
                                  <Tooltip title={title || ""} key={idx}>
                                    <IconButton
                                      size="small"
                                      onClick={() => onClick?.(row)}
                                    >
                                      {icon}
                                    </IconButton>
                                  </Tooltip>
                                ))}
                            </Box>
                          </TableCell>
                        )}
                      </>
                    )}
                  </TableRow>

                  {hasExtraFields && expandedRow === actualIndex && (
                    <TableRow>
                      <TableCell
                        colSpan={
                          normalizedFields.length +
                          (showExpandColumn ? 1 : 0) +
                          (actions?.length > 0 ? 1 : 0)
                        }
                        sx={{ padding: 0 }}
                      >
                        <Collapse in timeout="auto" unmountOnExit>
                          <Box
                            sx={{
                              borderTop: "1px solid var(--gray-200)",
                              backgroundColor: "var(--gray-50)",
                              paddingLeft: 10,
                              py: 2,
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 2,
                              alignItems: "left",
                              borderLeft: "4px solid var(--green-100)",
                              fontFamily: "inter",
                            }}
                          >
                            {extraFields.map(([key, value]) => (
                              <Box
                                key={key}
                                sx={{
                                  flex: "1 0 1",
                                  minWidth: 160,
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                <Typography
                                  variant="bodySmall"
                                  sx={{
                                    fontFamily: "'Inter', sans-serif",
                                    color: "var(--gray-800)",
                                    mb: 0.5,
                                    textTransform: "none",
                                  }}
                                >
                                  {key}
                                </Typography>
                                <Typography
                                  variant="bodyMedium"
                                  sx={{
                                    color: "var(--gray-800)",
                                    wordBreak: "break-word",
                                    fontFamily: "inherit",
                                  }}
                                >
                                  {value?.toString()}
                                </Typography>
                              </Box>
                            ))}

                            {Array.isArray(actionLink) &&
                              actionLink.length > 0 && (
                                <Box
                                  sx={{
                                    width: "100%",
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 3,
                                    px: 1,
                                    py: 1,
                                  }}
                                >
                                  {actionLink.map((label, idx) => (
                                    <Typography
                                      key={idx}
                                      variant="bodySmallStrong"
                                      sx={{
                                        color: "var(--link-blue)",
                                        fontWeight: 500,
                                        cursor: "pointer",
                                        fontFamily: "inter",
                                        "&:hover": {
                                          color: "var(--link-blue)",
                                        },
                                      }}
                                      onClick={() =>
                                        console.log(`Clicked: ${label}`)
                                      }
                                    >
                                      {label}
                                    </Typography>
                                  ))}
                                </Box>
                              )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}

          {/* Alphabet Filter Row */}
          {showAlphabetFilter && (
            <TableRow>
              <TableCell
                colSpan={
                  normalizedFields.length +
                  (showExpandColumn ? 1 : 0) +
                  (actions?.length > 0 ? 1 : 0)
                }
                sx={{
                  backgroundColor: "var( --brand-100)",
                  padding: "8px",
                  borderTop: "1px solid var(--gray-200)",
                }}
              >
                <Typography
                  variant="bodySmall"
                  sx={{
                    color: "var(--gray-800)",
                    fontFamily: "inter",
                    display: "flex",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  {" "}
                  {[
                    "ALL",
                    "#",
                    "A",
                    "B",
                    "C",
                    "D",
                    "E",
                    "F",
                    "G",
                    "H",
                    "I",
                    "J",
                    "K",
                    "L",
                    "M",
                    "N",
                    "O",
                    "P",
                    "Q",
                    "R",
                    "S",
                    "T",
                    "U",
                    "V",
                    "X",
                    "Y",
                    "Z",
                  ].map((letter) => (
                    <span
                      key={letter}
                      style={{
                        cursor: "pointer",
                        color:
                          letter === selectedLetter
                            ? "var(--link-blue)"
                            : "var(--link-blue)",
                        fontWeight:
                          letter === selectedLetter ? "bold" : "normal",
                        "&:hover": { textDecoration: "underline" },
                      }}
                      onClick={() => {
                        setSelectedLetter(
                          letter === selectedLetter
                            ? null
                            : letter === "ALL"
                              ? null
                              : letter
                        );
                        setPage(0); // Reset to first page on filter change
                      }}
                    >
                      {letter}
                    </span>
                  ))}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {filteredData.length > 10 && (
        <TablePagination
          count={filteredData.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      )}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 2, maxWidth: 250 }}>
          <Typography variant="bodyMedium">
            {popoverValue ?? "No remarks"}
          </Typography>
        </Box>
      </Popover>
    </Box>
  );
}
